import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Package, ShoppingBag, TrendingUp, AlertCircle, Plus, Edit, Trash2, Loader2,
  Crown, CheckCircle, XCircle, Truck, FileText, MapPin, Phone, Clock,
  ChevronDown, ChevronUp, Send, AlertTriangle, Settings, Euro, Scale, Box,
  RotateCcw, MessageSquare, ThumbsUp, ThumbsDown, Download, Printer, Eye, ExternalLink, Beaker,
  Wallet, Calendar, ArrowRight, Ban, CircleDollarSign, Info, History, RefreshCw, CreditCard, Sparkles,
  Upload, ShieldCheck, Link as LinkIcon, Store, Globe, AtSign, Users
} from 'lucide-react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

// ============================================
// SHIPPING SECTION COMPONENT
// ============================================

const ShippingOrderCard = ({ orderData, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [manualTracking, setManualTracking] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const [showLabelPreview, setShowLabelPreview] = useState(false);

  // Modale "Segna come Consegnato" con prova opzionale
  const [showDeliveredModal, setShowDeliveredModal] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);
  const [existingProof, setExistingProof] = useState(null); // { tracking_number, carrier, signed_url }
  const [proofLoaded, setProofLoaded] = useState(false);

  const { order, items, shipment, can_generate_label, can_mark_shipped, is_experience_only } = orderData;

  // Carica la prova esistente quando la card si espande (per ordini spediti/consegnati)
  useEffect(() => {
    if (!expanded || proofLoaded || is_experience_only) return;
    if (!['spedito', 'in_consegna', 'consegnato'].includes(order.status)) return;
    setProofLoaded(true);
    api.get(`/shipping/vendor/orders/${order.id}/shipping-proof`)
      .then(res => { if (res.data?.signed_url) setExistingProof(res.data); })
      .catch(() => {}); // nessuna prova ancora — non è un errore
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Can mark as delivered if shipped but not yet delivered
  const canMarkDelivered = shipment && 
    shipment.status !== 'delivered' && 
    ['picked_up', 'in_transit', 'out_for_delivery'].includes(shipment.status);

  // Get PDF URL for preview
  const getPdfUrl = () => {
    if (!shipment?.tracking_number) return null;
    const apiUrl = process.env.REACT_APP_API_URL || '';
    return `${apiUrl}/shipping/labels/${shipment.tracking_number}.pdf`;
  };

  const handleGenerateLabel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/shipping/labels/generate', {
        order_id: order.id,
        weight_kg: 1.0
      });
      toast.success(res.data.message);
      
      // Show preview after generation
      onRefresh();
      setTimeout(() => setShowLabelPreview(true), 500);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la generazione');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLabel = async (trackingNumber) => {
    const tracking = trackingNumber || shipment?.tracking_number;
    if (!tracking) {
      toast.error('Nessun tracking disponibile');
      return;
    }
    
    try {
      // Get base URL from env
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const pdfUrl = `${apiUrl}/shipping/labels/${tracking}.pdf`;
      
      // Open in new tab for download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.target = '_blank';
      link.download = `etichetta_${tracking}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Download etichetta avviato');
    } catch (error) {
      toast.error('Errore nel download dell\'etichetta');
    }
  };

  const handleManualTracking = async () => {
    if (!manualTracking.trim()) {
      toast.error('Inserisci un codice di tracking');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/shipping/tracking/manual', {
        order_id: order.id,
        tracking_number: manualTracking,
        carrier: 'Corriere esterno'
      });
      toast.success(res.data.message);
      setShowManualInput(false);
      setManualTracking('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkShipped = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/shipping/${shipment.id}/mark-shipped`);
      toast.success(res.data.message);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la conferma');
    } finally {
      setLoading(false);
    }
  };

  // Apre il modale "Consegnato" e carica eventuale prova già presente
  const handleMarkDelivered = async () => {
    if (!proofLoaded) {
      try {
        const res = await api.get(`/shipping/vendor/orders/${order.id}/shipping-proof`);
        if (res.data?.signed_url) setExistingProof(res.data);
      } catch {
        // Nessuna prova — ok
      }
      setProofLoaded(true);
    }
    setShowDeliveredModal(true);
  };

  // Conferma consegna: carica prova opzionale poi chiama mark-delivered
  const handleConfirmDelivered = async () => {
    setProofLoading(true);
    try {
      // 1. Carica prova se selezionata
      if (proofFile) {
        const formData = new FormData();
        formData.append('file', proofFile);
        const res = await api.post(
          `/shipping/vendor/orders/${order.id}/shipping-proof`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setExistingProof({
          tracking_number: res.data.tracking_number,
          carrier: res.data.carrier,
          signed_url: res.data.preview_url || res.data.signed_url
        });
      }
      // 2. Marca consegnato
      const res = await api.post(`/shipping/${shipment.id}/mark-delivered`);
      toast.success(res.data.message || 'Ordine segnato come consegnato');
      setShowDeliveredModal(false);
      setProofFile(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la conferma');
    } finally {
      setProofLoading(false);
    }
  };

  const handleCompleteExperience = async () => {
    if (!window.confirm('Confermi che l\'esperienza è stata completata? Questo renderà l\'ordine idoneo al payout.')) return;
    setLoading(true);
    try {
      const res = await api.post(`/shipping/experience/${order.id}/complete`);
      toast.success(res.data.message);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il completamento');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-800',
      paid: 'bg-blue-100 text-blue-800',
      in_preparazione: 'bg-yellow-100 text-yellow-800',
      spedito: 'bg-purple-100 text-purple-800',
      in_consegna: 'bg-indigo-100 text-indigo-800',
      consegnato: 'bg-green-100 text-green-800',
      problema_spedizione: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className={`border rounded-lg overflow-hidden bg-white ${order.active_dispute ? 'border-red-400' : 'border-border'}`}>
      {/* Banner chargeback */}
      {order.active_dispute && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center gap-2 text-red-700 text-xs font-medium">
          <span>⚠️</span>
          <span>
            Chargeback aperto — {order.active_dispute.reason?.replace(/_/g, ' ')} · €{order.active_dispute.amount?.toFixed(2)}
            {order.active_dispute.due_by && (
              <> · Scadenza evidence: {new Date(order.active_dispute.due_by).toLocaleDateString('it-IT')}</>
            )}
          </span>
        </div>
      )}
      {/* Header */}
      <div
        className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <p className="font-mono text-sm font-medium">{order.order_number}</p>
              <p className="text-xs text-text-muted">
                {new Date(order.created_at).toLocaleDateString('it-IT', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <Badge className={getStatusBadge(order.status)}>
              {order.status_display?.label || order.status}
            </Badge>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-semibold">€{order.producer_total?.toFixed(2)}</p>
              <p className="text-xs text-text-muted">{items?.length || 0} prodotti</p>
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-text-muted" />
            ) : (
              <ChevronDown className="w-5 h-5 text-text-muted" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-border p-4 space-y-4">
          {/* Destinatario */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-muted/30 p-3 rounded-lg">
              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4" />
                Destinatario
              </h4>
              <p className="font-medium">{order.shipping_name}</p>
              <p className="text-sm text-text-muted">{order.shipping_address}</p>
              <p className="text-sm text-text-muted">
                {order.shipping_postal_code} {order.shipping_city}
              </p>
              {order.shipping_phone && (
                <p className="text-sm text-text-muted flex items-center gap-1 mt-1">
                  <Phone className="w-3 h-3" />
                  {order.shipping_phone}
                </p>
              )}
            </div>

            {/* Prodotti */}
            <div className="bg-muted/30 p-3 rounded-lg">
              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                <Package className="w-4 h-4" />
                Prodotti
              </h4>
              <div className="space-y-1">
                {items?.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span>{item.product_name} x{item.quantity}</span>
                    <span className="text-text-muted">€{item.total_price?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tracking Info */}
          {shipment && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <h4 className="text-sm font-medium flex items-center gap-2 mb-2 text-blue-800">
                <Truck className="w-4 h-4" />
                Spedizione
                {shipment.label_url && (
                  <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                    <Beaker className="w-3 h-3 mr-1" />
                    BETA
                  </Badge>
                )}
              </h4>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm">
                    <span className="text-text-muted">Tracking:</span>{' '}
                    <span className="font-mono font-medium">{shipment.tracking_number}</span>
                  </p>
                  <p className="text-xs text-text-muted">
                    {shipment.status_display?.description}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-blue-700">
                    {shipment.status_display?.label}
                  </Badge>
                </div>
              </div>
              
              {/* Label Actions - BETA Feature */}
              {shipment.tracking_number && shipment.label_url && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs text-amber-600 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Funzionalità BETA: Etichetta di test per sviluppo. Non valida per spedizioni reali.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => setShowLabelPreview(true)}
                      className="gap-2 text-blue-700 border-blue-300 hover:bg-blue-100"
                      data-testid="preview-label-btn"
                    >
                      <Eye className="w-4 h-4" />
                      Anteprima
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadLabel()}
                      className="gap-2 text-blue-700 border-blue-300 hover:bg-blue-100"
                      data-testid="download-label-btn"
                    >
                      <Download className="w-4 h-4" />
                      Scarica PDF
                    </Button>
                    <Button 
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const pdfUrl = getPdfUrl();
                        if (pdfUrl) window.open(pdfUrl, '_blank');
                      }}
                      className="gap-2 text-blue-600 hover:bg-blue-100"
                      data-testid="open-label-btn"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Apri in nuova tab
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Label Preview Dialog */}
          <Dialog open={showLabelPreview} onOpenChange={setShowLabelPreview}>
            <DialogContent className="max-w-4xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Anteprima Etichetta di Spedizione
                  <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300 ml-2">
                    <Beaker className="w-3 h-3 mr-1" />
                    BETA
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <strong>Ambiente di Test:</strong> Questa etichetta è generata in modalità sandbox. 
                  Il tracking number non è valido per spedizioni reali.
                </p>
              </div>
              
              {getPdfUrl() && (
                <div className="border rounded-lg overflow-hidden bg-gray-100">
                  <iframe
                    src={getPdfUrl()}
                    className="w-full h-[500px]"
                    title="Anteprima Etichetta"
                  />
                </div>
              )}
              
              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Tracking: <span className="font-mono font-medium">{shipment?.tracking_number}</span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => setShowLabelPreview(false)}
                  >
                    Chiudi
                  </Button>
                  <Button 
                    onClick={() => handleDownloadLabel()}
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Scarica PDF
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
            {/* Messaggio per EXPERIENCE - no spedizione necessaria */}
            {is_experience_only && (
              <div className="w-full bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-purple-800">Esperienza - Nessuna spedizione richiesta</p>
                    <p className="text-sm text-purple-600 mt-1">
                      Contatta direttamente il cliente per fornire i dettagli dell'evento (orario, punto di ritrovo, istruzioni).
                    </p>
                    <p className="text-xs text-purple-500 mt-2">
                      Cliente: {order.shipping_name} • {order.shipping_phone || 'Telefono non fornito'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Pulsante Completa Esperienza - solo per EXPERIENCE in stato paid/in_preparazione */}
            {is_experience_only && ['paid', 'in_preparazione'].includes(order.status) && (
              <Button 
                onClick={handleCompleteExperience}
                disabled={loading}
                className="gap-2 bg-purple-600 hover:bg-purple-700"
                data-testid="complete-experience-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Completa Esperienza
              </Button>
            )}
            
            {/* Genera Etichetta - nascosto per EXPERIENCE */}
            {can_generate_label && !is_experience_only && (
              <>
                <Button 
                  onClick={handleGenerateLabel} 
                  disabled={loading}
                  className="gap-2"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  Genera Etichetta
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={() => setShowManualInput(!showManualInput)}
                  className="gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Tracking Manuale
                </Button>
              </>
            )}

            {/* Conferma Spedizione (senza prova — fallback) */}
            {can_mark_shipped && (
              <Button
                onClick={handleMarkShipped}
                disabled={loading}
                variant="outline"
                className="gap-2 border-green-600 text-green-700 hover:bg-green-50"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Conferma senza prova
              </Button>
            )}

            {/* Segna come Consegnato — apre modale con upload prova opzionale */}
            {canMarkDelivered && (
              <Button
                onClick={handleMarkDelivered}
                disabled={loading}
                variant="outline"
                className="gap-2 border-green-600 text-green-700 hover:bg-green-50"
                data-testid="mark-delivered-btn"
              >
                <CheckCircle className="w-4 h-4" />
                Segna come Consegnato
              </Button>
            )}

            {/* Modale conferma consegna */}
            <Dialog open={showDeliveredModal} onOpenChange={(open) => { setShowDeliveredModal(open); if (!open) setProofFile(null); }}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Conferma Consegna
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-2">
                  <p className="text-sm text-text-muted">
                    Stai per segnare l'ordine <span className="font-mono font-medium">{order.order_number}</span> come consegnato.
                  </p>

                  {/* Prova esistente */}
                  {existingProof?.signed_url && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                      <p className="font-medium text-green-800 flex items-center gap-1 mb-1">
                        <ShieldCheck className="w-4 h-4" />
                        Prova di spedizione già caricata
                      </p>
                      {existingProof.tracking_number && (
                        <p className="text-green-700 text-xs">Tracking: <span className="font-mono">{existingProof.tracking_number}</span></p>
                      )}
                      <a
                        href={existingProof.signed_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-green-700 hover:underline text-xs mt-1"
                      >
                        <LinkIcon className="w-3 h-3" />
                        Visualizza documento
                      </a>
                    </div>
                  )}

                  {/* Upload prova (opzionale) */}
                  <div className="border border-dashed border-gray-300 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Upload className="w-4 h-4 text-gray-500" />
                      Carica prova di spedizione <span className="text-xs text-text-muted font-normal">(facoltativo)</span>
                    </p>
                    <p className="text-xs text-gray-500 flex items-start gap-1">
                      <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      Questo documento viene allegato automaticamente come prova in caso di contestazioni di pagamento.
                    </p>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => setProofFile(e.target.files?.[0] || null)}
                      className="block w-full text-xs text-gray-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 cursor-pointer"
                    />
                    {proofFile && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {proofFile.name} ({(proofFile.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    )}
                    <p className="text-xs text-gray-400">PDF, JPG, PNG · max 10 MB</p>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    variant="ghost"
                    onClick={() => { setShowDeliveredModal(false); setProofFile(null); }}
                    disabled={proofLoading}
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={handleConfirmDelivered}
                    disabled={proofLoading}
                    className="gap-2 bg-green-600 hover:bg-green-700"
                  >
                    {proofLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    {proofFile ? 'Carica e Conferma Consegna' : 'Conferma Consegna'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Stato già spedito */}
            {order.status === 'spedito' && !canMarkDelivered && (
              <Badge variant="outline" className="text-green-700 gap-1">
                <CheckCircle className="w-3 h-3" />
                Spedito
              </Badge>
            )}

            {order.status === 'consegnato' && (
              <Badge className="bg-green-100 text-green-800 gap-1">
                <CheckCircle className="w-3 h-3" />
                Consegnato
              </Badge>
            )}
          </div>

          {/* Prova di spedizione — visibile dopo consegna */}
          {!is_experience_only && order.status === 'consegnato' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-green-800 flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4" />
                Prova di spedizione
              </p>
              {existingProof?.signed_url ? (
                <div className="space-y-1">
                  {existingProof.tracking_number && (
                    <p className="text-green-700 text-xs">
                      Tracking: <span className="font-mono font-medium">{existingProof.tracking_number}</span>
                      {existingProof.carrier && <> · {existingProof.carrier}</>}
                    </p>
                  )}
                  <a
                    href={existingProof.signed_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-green-700 hover:underline text-xs"
                  >
                    <LinkIcon className="w-3 h-3" />
                    Visualizza documento
                  </a>
                </div>
              ) : (
                <p className="text-xs text-green-600 italic">Nessuna prova caricata per questo ordine.</p>
              )}
            </div>
          )}

          {/* Manual Tracking Input */}
          {showManualInput && (
            <div className="flex gap-2 items-end pt-2">
              <div className="flex-1">
                <Label className="text-xs">Codice Tracking</Label>
                <Input
                  value={manualTracking}
                  onChange={(e) => setManualTracking(e.target.value)}
                  placeholder="Es: BRT1234567890"
                  className="mt-1"
                />
              </div>
              <Button onClick={handleManualTracking} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva'}
              </Button>
            </div>
          )}

          {/* Note */}
          <p className="text-xs text-text-muted italic">
            💡 Ricorda: Tu sei il mittente della spedizione. DalSud gestisce solo la piattaforma.
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// RETURN REQUEST CARD COMPONENT
// ============================================

const ReturnRequestCard = ({ returnData, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [responseAction, setResponseAction] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState('');

  const getStatusBadge = (status) => {
    const styles = {
      return_pending_approval: 'bg-orange-100 text-orange-800',
      return_requested: 'bg-yellow-100 text-yellow-800',
      return_approved: 'bg-green-100 text-green-800',
      return_rejected: 'bg-red-100 text-red-800',
      return_in_transit: 'bg-blue-100 text-blue-800',
      return_received: 'bg-purple-100 text-purple-800',
      return_completed: 'bg-green-100 text-green-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      return_pending_approval: 'In attesa (FOOD)',
      return_requested: 'In attesa',
      return_approved: 'Approvato',
      return_rejected: 'Rifiutato',
      return_in_transit: 'In transito',
      return_received: 'Ricevuto',
      return_completed: 'Completato'
    };
    return labels[status] || status;
  };

  const handleRespond = async () => {
    if (!responseAction) return;
    
    if (responseAction === 'reject' && responseMessage.length < 20) {
      toast.error('Fornisci una motivazione dettagliata per il rifiuto (minimo 20 caratteri)');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        action: responseAction,
        response_message: responseMessage,
        return_instructions: responseAction === 'approve' ? responseMessage : null,
        refund_amount: refundAmount ? parseFloat(refundAmount) : null,
        refund_method: refundMethod || null
      };
      
      await api.post(`/returns/vendor/${returnData.id}/respond`, data);
      toast.success(responseAction === 'approve' ? 'Reso approvato' : 'Risposta inviata');
      setResponseDialogOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la risposta');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setLoading(true);
    try {
      await api.post(`/returns/vendor/${returnData.id}/update-status`, {
        status: newStatus
      });
      toast.success('Stato aggiornato');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-white">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-muted/30"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RotateCcw className="w-5 h-5 text-orange-500" />
            <div>
              <p className="font-medium">{returnData.order_number}</p>
              <p className="text-xs text-text-muted">{returnData.customer_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={getStatusBadge(returnData.status)}>
              {getStatusLabel(returnData.status)}
            </Badge>
            <span className="text-xs text-text-muted">{formatDate(returnData.created_at)}</span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
        
        {/* Deadline warning */}
        {['return_requested', 'return_pending_approval'].includes(returnData.status) && returnData.is_within_window && (
          <div className="mt-2 text-xs text-orange-600 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Scadenza recesso: {formatDate(returnData.deadline_date)}
          </div>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-border p-4 space-y-4">
          {/* Items */}
          <div>
            <p className="text-xs font-medium text-text-muted mb-2">Prodotti da restituire:</p>
            <div className="space-y-1">
              {returnData.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm bg-muted/30 p-2 rounded">
                  <span>{item.product_name}</span>
                  <span className="text-text-muted">x{item.quantity} - €{item.unit_price}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Reason */}
          {returnData.reason && (
            <div>
              <p className="text-xs font-medium text-text-muted mb-1">Motivo cliente:</p>
              <p className="text-sm bg-muted/30 p-2 rounded">{returnData.reason}</p>
            </div>
          )}

          {/* Customer info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-text-muted">Cliente</p>
              <p>{returnData.customer_email}</p>
            </div>
            <div>
              <p className="text-xs text-text-muted">Indirizzo</p>
              <p>{returnData.shipping_city}</p>
            </div>
          </div>

          {/* Actions based on status */}
          {['return_requested', 'return_pending_approval'].includes(returnData.status) && (
            <div className="flex gap-2 pt-2 border-t border-border">
              <Dialog open={responseDialogOpen} onOpenChange={setResponseDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    size="sm"
                    className="gap-1 bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      setResponseAction('approve');
                      // Pre-popola importo dalla policy calcolata al momento della richiesta
                      setRefundAmount(returnData.refund_amount != null ? String(returnData.refund_amount) : '');
                      // Default: rimborso automatico via Stripe
                      setRefundMethod('stripe');
                    }}
                    data-testid="approve-return-btn"
                  >
                    <ThumbsUp className="w-3 h-3" />
                    Approva
                  </Button>
                </DialogTrigger>
                <DialogTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    className="gap-1"
                    onClick={() => setResponseAction('reject')}
                  >
                    <ThumbsDown className="w-3 h-3" />
                    Rifiuta
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {responseAction === 'approve' ? 'Approva Reso' : 'Rifiuta Reso'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {responseAction === 'approve' ? (
                      <>
                        {/* Info policy rimborso calcolata automaticamente */}
                        {returnData.refund_amount != null && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-blue-800">
                              <p className="font-medium">
                                Rimborso calcolato automaticamente: <strong>€{Number(returnData.refund_amount).toFixed(2)}</strong>
                              </p>
                              <p className="text-xs text-blue-600 mt-0.5">
                                {returnData.macro_type === 'EXPERIENCE'
                                  ? 'Basato sulla policy di cancellazione in base alla data evento.'
                                  : returnData.macro_type === 'ARTIGIANATO'
                                  ? 'Diritto di recesso UE: rimborso 100% del valore prodotto.'
                                  : 'Calcolato dalla policy di reso applicabile.'}
                                {' '}Il rimborso verrà elaborato via Stripe al completamento del reso.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Per EXPERIENCE: nessuna spedizione richiesta, solo conferma */}
                        {returnData.macro_type !== 'EXPERIENCE' && (
                          <div>
                            <Label>Istruzioni per la restituzione *</Label>
                            <Textarea
                              value={responseMessage}
                              onChange={(e) => setResponseMessage(e.target.value)}
                              placeholder="Es: Spedire il prodotto a Via Roma 1, 80100 Napoli. Inserire il numero d'ordine nel pacco."
                              rows={3}
                              className="mt-1"
                            />
                          </div>
                        )}
                        {returnData.macro_type === 'EXPERIENCE' && (
                          <div>
                            <Label>Messaggio al cliente (facoltativo)</Label>
                            <Textarea
                              value={responseMessage}
                              onChange={(e) => setResponseMessage(e.target.value)}
                              placeholder="Es: Cancellazione confermata, il rimborso sarà elaborato entro 5-7 giorni lavorativi."
                              rows={2}
                              className="mt-1"
                            />
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>
                              Importo rimborso (€)
                              {returnData.refund_amount != null && (
                                <span className="ml-1 text-xs text-blue-600 font-normal">(pre-calcolato)</span>
                              )}
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={refundAmount}
                              onChange={(e) => setRefundAmount(e.target.value)}
                              placeholder="Es: 25.00"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Metodo rimborso</Label>
                            <Select value={refundMethod} onValueChange={setRefundMethod}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Seleziona..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="stripe">Rimborso Stripe (automatico)</SelectItem>
                                <SelectItem value="bonifico">Bonifico bancario</SelectItem>
                                <SelectItem value="buono">Buono acquisto</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div>
                        <Label>Motivazione del rifiuto *</Label>
                        <Textarea
                          value={responseMessage}
                          onChange={(e) => setResponseMessage(e.target.value)}
                          placeholder="Spiega il motivo del rifiuto (minimo 20 caratteri)"
                          rows={3}
                          className="mt-1"
                        />
                        <p className="text-xs text-text-muted mt-1">
                          Nota: Il rifiuto deve essere giustificato secondo la normativa UE.
                        </p>
                      </div>
                    )}
                    <Button 
                      onClick={handleRespond} 
                      disabled={loading}
                      className="w-full"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Conferma'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Status updates for approved returns */}
          {returnData.status === 'return_approved' && (
            <div className="flex gap-2 pt-2 border-t border-border">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleUpdateStatus('return_received')}
                disabled={loading}
              >
                Prodotto Ricevuto
              </Button>
            </div>
          )}

          {returnData.status === 'return_received' && (
            <div className="flex gap-2 pt-2 border-t border-border">
              <Button 
                size="sm" 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleUpdateStatus('return_completed')}
                disabled={loading}
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Rimborso Completato
              </Button>
            </div>
          )}

          {/* Vendor response shown */}
          {returnData.vendor_response && returnData.status !== 'return_requested' && (
            <div className={`p-3 rounded-lg ${
              returnData.status === 'return_approved' ? 'bg-green-50' : 
              returnData.status === 'return_rejected' ? 'bg-red-50' : 'bg-muted/50'
            }`}>
              <p className="text-xs font-medium mb-1">La tua risposta:</p>
              <p className="text-sm">{returnData.vendor_response}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN PRODUCER DASHBOARD
// ============================================

const ProducerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [categories, setCategories] = useState([]);
  const [plans, setPlans] = useState([]);
  const [shippingOrders, setShippingOrders] = useState([]);
  const [shippingSettings, setShippingSettings] = useState(null);
  const [returnRequests, setReturnRequests] = useState([]);
  const [returnsPendingCount, setReturnsPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [returnsLoading, setReturnsLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'overview');
  const [productForm, setProductForm] = useState({
    name: '', slug: '', category_id: '', description: '', story: '',
    price: '', compare_price: '', inventory: '', unit: '', image_url: '', status: 'draft',
    event_date: '', location: ''  // Campi per EXPERIENCE
  });
  const [shippingForm, setShippingForm] = useState({
    shipping_cost_type: 'free',
    shipping_flat_rate: '',
    shipping_per_kg: '',
    shipping_per_item: '',
    shipping_free_threshold: ''
  });
  // Payment dashboard state
  const [paymentsDashboard, setPaymentsDashboard] = useState(null);
  const [paymentsOrders, setPaymentsOrders] = useState({ items: [], total: 0 });
  const [payoutsHistory, setPayoutsHistory] = useState([]);
  const [vendorRefunds, setVendorRefunds] = useState(null);
  const [retryingRefundId, setRetryingRefundId] = useState(null);
  const [paymentsFilter, setPaymentsFilter] = useState('');

  // Stripe Connect state
  const [stripeConnect, setStripeConnect] = useState(null);
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);
  const [stripeConnectWorking, setStripeConnectWorking] = useState(false);

  // Vetrina state
  const [storefrontForm, setStorefrontForm] = useState({
    description: '', story: '', website: '', instagram: '', facebook: '',
    business_hours: '', address: '', city: '', postal_code: '', phone: '',
  });
  const [storefrontSaving, setStorefrontSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const logoInputRef = React.useRef(null);
  const coverInputRef = React.useRef(null);
  const productImageRef = React.useRef(null);
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState(null);

  const subscriptionStatus = searchParams.get('subscription');
  const sessionId = searchParams.get('session_id');
  const stripeOnboardingReturn = searchParams.get('stripe'); // "success" quando vendor torna da onboarding

  // State per abbonamento Pro
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  // Fetch subscription status
  const fetchSubscriptionStatus = async () => {
    try {
      const res = await api.get('/subscriptions/my-subscription');
      setSubscriptionData(res.data.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  // Funzione per sincronizzare lo stato con Stripe
  const syncSubscriptionWithStripe = async () => {
    try {
      setLoading(true);
      const res = await api.post('/subscriptions/sync');
      if (res.data.data?.synced) {
        toast.success('Stato abbonamento sincronizzato');
        await fetchSubscriptionStatus();
      }
    } catch (error) {
      console.error('Error syncing subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subscriptionStatus === 'success' && sessionId) {
      const checkSubscription = async () => {
        try {
          // Verifica lo stato del checkout e sincronizza con Stripe
          const statusRes = await api.get(`/subscriptions/checkout-status/${sessionId}`);
          if (statusRes.data.data?.status === 'complete') {
            toast.success('Abbonamento Pro attivato con successo!');
            // Refresh dashboard data
            await fetchDashboard();
            // Sync subscription data
            await fetchSubscriptionStatus();
            // Remove query params from URL
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch (error) {
          console.error('Subscription check error:', error);
        }
      };
      checkSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionStatus, sessionId]);

  // Fetch subscription on mount
  useEffect(() => {
    if (dashboard?.producer?.id) {
      fetchSubscriptionStatus();
    }
  }, [dashboard?.producer?.id]);

  // Popola form vetrina quando i dati dashboard sono disponibili
  useEffect(() => {
    if (dashboard?.producer) {
      const p = dashboard.producer;
      setStorefrontForm({
        description: p.description || '',
        story: p.story || '',
        website: p.website || '',
        instagram: p.instagram || '',
        facebook: p.facebook || '',
        business_hours: p.business_hours || '',
        address: p.address || '',
        city: p.city || '',
        postal_code: p.postal_code || '',
        phone: p.phone || '',
      });
    }
  }, [dashboard?.producer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    setLogoUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/upload/producer/${dashboard.producer.id}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo aggiornato');
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverPreview(URL.createObjectURL(file));
    setCoverUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/upload/producer/${dashboard.producer.id}/cover`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Cover aggiornata');
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload cover');
    } finally {
      setCoverUploading(false);
    }
  };

  const handleStorefrontSave = async () => {
    setStorefrontSaving(true);
    try {
      await api.put('/producers/me', storefrontForm);
      toast.success('Vetrina aggiornata');
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setStorefrontSaving(false);
    }
  };

  // Sync Stripe Connect dopo ritorno da onboarding (?stripe=success)
  useEffect(() => {
    if (stripeOnboardingReturn === 'success' && dashboard?.producer?.id) {
      const syncAndRefresh = async () => {
        try {
          const res = await api.post(`/vendors/${dashboard.producer.id}/stripe/connect/sync`);
          setStripeConnect(res.data);
          if (res.data.changed) {
            toast.success('Account Stripe aggiornato correttamente.');
          } else {
            toast.info('Stato Stripe verificato. Se il badge non si aggiorna, clicca "Sincronizza".');
          }
        } catch (err) {
          console.error('Stripe Connect sync error:', err);
          toast.error('Impossibile sincronizzare lo stato Stripe. Usa il pulsante "Sincronizza".');
        }
        window.history.replaceState({}, '', window.location.pathname + '?tab=payments');
      };
      syncAndRefresh();
    }
  }, [stripeOnboardingReturn, dashboard?.producer?.id]);

  const handleStripeConnectSync = async (producerId) => {
    setStripeConnectWorking(true);
    try {
      const res = await api.post(`/vendors/${producerId}/stripe/connect/sync`);
      setStripeConnect(res.data);
      toast.success(res.data.changed ? 'Stato Stripe aggiornato.' : 'Stato Stripe già aggiornato.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante la sincronizzazione Stripe.');
    } finally {
      setStripeConnectWorking(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const [dashRes, catRes, planRes] = await Promise.all([
        api.get('/producers/me/dashboard'),
        api.get('/catalog/categories'),
        api.get('/payments/plans')
      ]);
      setDashboard(dashRes.data);
      setCategories(catRes.data);
      setPlans(planRes.data);
    } catch (error) {
      if (error.response?.status === 404) {
        navigate('/diventa-produttore');
      }
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShippingOrders = async () => {
    setShippingLoading(true);
    try {
      const res = await api.get('/shipping/producer/orders?page_size=50');
      setShippingOrders(res.data.items || []);
    } catch (error) {
      console.error('Error fetching shipping orders:', error);
    } finally {
      setShippingLoading(false);
    }
  };

  const fetchShippingSettings = async () => {
    try {
      const res = await api.get('/shipping/settings');
      setShippingSettings(res.data);
      setShippingForm({
        shipping_cost_type: res.data.shipping_cost_type || 'free',
        shipping_flat_rate: res.data.shipping_flat_rate?.toString() || '',
        shipping_per_kg: res.data.shipping_per_kg?.toString() || '',
        shipping_per_item: res.data.shipping_per_item?.toString() || '',
        shipping_free_threshold: res.data.shipping_free_threshold?.toString() || ''
      });
    } catch (error) {
      console.error('Error fetching shipping settings:', error);
    }
  };

  const fetchReturnRequests = async () => {
    setReturnsLoading(true);
    try {
      const res = await api.get('/returns/vendor/requests?page_size=50');
      setReturnRequests(res.data.items || []);
      setReturnsPendingCount(res.data.pending_count || 0);
    } catch (error) {
      console.error('Error fetching return requests:', error);
    } finally {
      setReturnsLoading(false);
    }
  };

  const handleSaveShippingSettings = async (e) => {
    e.preventDefault();
    setSettingsLoading(true);
    try {
      const data = {
        shipping_cost_type: shippingForm.shipping_cost_type,
        shipping_flat_rate: parseFloat(shippingForm.shipping_flat_rate) || 0,
        shipping_per_kg: parseFloat(shippingForm.shipping_per_kg) || 0,
        shipping_per_item: parseFloat(shippingForm.shipping_per_item) || 0,
        shipping_free_threshold: parseFloat(shippingForm.shipping_free_threshold) || 0
      };
      await api.put('/shipping/settings', data);
      toast.success('Impostazioni spedizione salvate');
      fetchShippingSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'shipping' && dashboard?.producer?.status === 'approved') {
      fetchShippingOrders();
    }
    if (activeTab === 'shipping-settings' && dashboard?.producer?.status === 'approved') {
      fetchShippingSettings();
    }
    if (activeTab === 'returns' && dashboard?.producer?.status === 'approved') {
      fetchReturnRequests();
    }
    if (activeTab === 'payments' && dashboard?.producer?.status === 'approved') {
      fetchPaymentsDashboard();
      fetchStripeConnectStatus(dashboard.producer.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dashboard]);

  // Fetch payments dashboard data
  const fetchPaymentsDashboard = async () => {
    setPaymentsLoading(true);
    try {
      const [dashRes, ordersRes, historyRes, refundsRes] = await Promise.all([
        api.get('/vendor/dashboard'),
        api.get('/vendor/orders', { params: { limit: 20, payment_status: paymentsFilter || undefined } }),
        api.get('/vendor/payouts'),
        api.get('/vendor/refunds', { params: { limit: 20 } })
      ]);
      setPaymentsDashboard(dashRes.data.data);
      setPaymentsOrders(ordersRes.data.data);
      setPayoutsHistory(historyRes.data.data.payouts);
      setVendorRefunds(refundsRes.data.data);
    } catch (error) {
      console.error('Error fetching payments data:', error);
      toast.error('Errore nel caricamento dei dati pagamenti');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchStripeConnectStatus = async (producerId) => {
    setStripeConnectLoading(true);
    try {
      const res = await api.get(`/vendors/${producerId}/stripe/connect`);
      setStripeConnect(res.data);
    } catch (error) {
      console.error('Error fetching Stripe Connect status:', error);
    } finally {
      setStripeConnectLoading(false);
    }
  };

  const handleStripeOnboarding = async (producerId) => {
    setStripeConnectWorking(true);
    try {
      const origin = window.location.origin;
      const res = await api.post(`/vendors/${producerId}/stripe/connect`, {
        refresh_url: `${origin}/dashboard-produttore?tab=payments`,
        return_url: `${origin}/dashboard-produttore?tab=payments&stripe=success`,
      });
      if (res.data.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la connessione a Stripe');
      setStripeConnectWorking(false);
    }
  };

  const handleRetryRefund = async (refundId) => {
    setRetryingRefundId(refundId);
    try {
      const res = await api.post(`/vendor/refunds/${refundId}/retry`);
      if (res.data.success) {
        toast.success(res.data.message || 'Rimborso completato con successo');
      } else {
        toast.error(res.data.message || 'Rimborso fallito');
      }
      // Ricarica lo storico rimborsi
      const refundsRes = await api.get('/vendor/refunds', { params: { limit: 20 } });
      setVendorRefunds(refundsRes.data.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il tentativo di rimborso');
    } finally {
      setRetryingRefundId(null);
    }
  };

  // Refetch orders when filter changes
  const handlePaymentsFilterChange = async (filter) => {
    setPaymentsFilter(filter);
    try {
      const res = await api.get('/vendor/orders', { 
        params: { limit: 20, payment_status: filter || undefined } 
      });
      setPaymentsOrders(res.data.data);
    } catch (error) {
      console.error('Error filtering orders:', error);
    }
  };

  const handleUpgradePlan = async () => {
    try {
      const res = await api.post('/subscriptions/subscribe', {
        origin_url: window.location.origin,
        billing_name: producer?.name || '',
        billing_email: user?.email || ''
      });
      if (res.data.data?.checkout_url) {
        // Use window.top to avoid iframe issues with Stripe Checkout
        if (window.top) {
          window.top.location.href = res.data.data.checkout_url;
        } else {
          window.location.href = res.data.data.checkout_url;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'upgrade');
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        compare_price: productForm.compare_price ? parseFloat(productForm.compare_price) : null,
        inventory: parseInt(productForm.inventory) || 0,
        event_date: productForm.event_date ? new Date(productForm.event_date).toISOString() : null,
        location: productForm.location || null
      };

      let productId;
      if (editingProduct) {
        await api.put(`/producers/me/products/${editingProduct.id}`, data);
        productId = editingProduct.id;
        toast.success('Prodotto aggiornato');
      } else {
        const res = await api.post('/producers/me/products', data);
        productId = res.data.id;
        toast.success('Prodotto creato');
      }

      // Carica immagine se selezionata
      if (productImageFile && productId) {
        const form = new FormData();
        form.append('file', productImageFile);
        await api.post(`/upload/product/${productId}/image`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setProductDialogOpen(false);
      setEditingProduct(null);
      resetProductForm();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
    try {
      await api.delete(`/producers/me/products/${productId}`);
      toast.success('Prodotto eliminato');
      fetchDashboard();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setProductImageFile(null);
    setProductImagePreview(product.image_url || null);
    setProductForm({
      name: product.name,
      slug: product.slug,
      category_id: product.category_id || '',
      description: product.description || '',
      story: product.story || '',
      price: product.price.toString(),
      compare_price: product.compare_price?.toString() || '',
      inventory: product.inventory.toString(),
      unit: product.unit || '',
      image_url: product.image_url || '',
      status: product.status,
      event_date: product.event_date ? product.event_date.slice(0, 16) : '',  // Format for datetime-local
      location: product.location || ''
    });
    setProductDialogOpen(true);
  };

  const resetProductForm = () => {
    setProductImageFile(null);
    setProductImagePreview(null);
    setProductForm({
      name: '', slug: '', category_id: '', description: '', story: '',
      price: '', compare_price: '', inventory: '', unit: '', image_url: '', status: 'draft',
      event_date: '', location: ''
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!dashboard) return null;

  const { producer, stats, products, recent_orders } = dashboard;
  const isPending = producer.status === 'pending';
  const isRejected = producer.status === 'rejected';
  const isApproved = producer.status === 'approved';

  // Count orders needing action
  const ordersNeedingAction = shippingOrders.filter(
    o => o.can_generate_label || o.can_mark_shipped
  ).length;

  return (
    <div className="min-h-screen bg-background" data-testid="producer-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-text-primary">
              Ciao, {producer.name}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              {isPending && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">
                  <AlertCircle className="w-3 h-3" />
                  In attesa di approvazione
                </span>
              )}
              {isRejected && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">
                  <XCircle className="w-3 h-3" />
                  Richiesta rifiutata
                </span>
              )}
              {isApproved && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-secondary/10 text-secondary">
                  <CheckCircle className="w-3 h-3" />
                  Approvato
                </span>
              )}
              {stats.plan_type === 'pro' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-accent/20 text-accent-foreground font-semibold">
                  <Crown className="w-3 h-3" />
                  Pro
                </span>
              )}
            </div>
          </div>

          {stats.plan_type !== 'pro' && isApproved && (
            <Button onClick={handleUpgradePlan} className="gap-2" data-testid="upgrade-btn">
              <Crown className="w-4 h-4" />
              Passa a Pro
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded-xl border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active_products}</p>
                <p className="text-xs text-text-muted">Prodotti Attivi</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{recent_orders.length}</p>
                <p className="text-xs text-text-muted">Ordini Recenti</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold">{(stats.commission_rate * 100).toFixed(0)}%</p>
                <p className="text-xs text-text-muted">Commissione</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-border">
            <p className="text-sm text-text-muted mb-1">Limite Prodotti</p>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold">{stats.total_products}</span>
              <span className="text-text-muted">/ {stats.product_limit}</span>
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-border">
            <TabsTrigger value="vetrina" className="gap-2">
              <Store className="w-4 h-4" />
              Vetrina
            </TabsTrigger>
            <TabsTrigger value="overview" className="gap-2">
              <Package className="w-4 h-4" />
              Prodotti
            </TabsTrigger>
            <TabsTrigger value="shipping" className="gap-2" disabled={!isApproved}>
              <Truck className="w-4 h-4" />
              Spedizioni
              {ordersNeedingAction > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">
                  {ordersNeedingAction}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="shipping-settings" className="gap-2" disabled={!isApproved}>
              <Settings className="w-4 h-4" />
              Costi Spedizione
            </TabsTrigger>
            <TabsTrigger value="returns" className="gap-2" disabled={!isApproved}>
              <RotateCcw className="w-4 h-4" />
              Resi
              {returnsPendingCount > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">
                  {returnsPendingCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="payments" className="gap-2" disabled={!isApproved}>
              <Euro className="w-4 h-4" />
              Pagamenti
            </TabsTrigger>
            <TabsTrigger value="subscription" className="gap-2" disabled={!isApproved}>
              <Crown className="w-4 h-4" />
              Abbonamento
              {subscriptionData?.is_pro && (
                <Badge variant="secondary" className="ml-1 text-xs bg-accent/20">Pro</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="orders" className="gap-2">
              <ShoppingBag className="w-4 h-4" />
              Ordini
            </TabsTrigger>
            <TabsTrigger value="ledger" className="gap-2" disabled={!isApproved}>
              <Wallet className="w-4 h-4" />
              Saldo
            </TabsTrigger>
          </TabsList>

          {/* Vetrina Tab */}
          <TabsContent value="vetrina">
            <div className="space-y-6">
              {/* Logo e Cover */}
              <div className="bg-white rounded-xl border border-border p-6">
                <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                  <Store className="w-5 h-5" />
                  Immagini vetrina
                </h2>

                {/* Cover */}
                <div className="mb-6">
                  <p className="text-sm font-medium mb-2">Immagine di copertina</p>
                  <div
                    className="relative w-full h-40 rounded-xl overflow-hidden border-2 border-dashed border-border bg-gray-50 cursor-pointer hover:border-primary transition-colors"
                    onClick={() => coverInputRef.current?.click()}
                  >
                    {(coverPreview || dashboard?.producer?.cover_url) ? (
                      <img
                        src={coverPreview || dashboard.producer.cover_url}
                        alt="Cover"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-text-secondary">
                        <Upload className="w-8 h-8 mb-2" />
                        <span className="text-sm">Carica copertina</span>
                        <span className="text-xs mt-1">JPG, PNG, WebP — max 5MB</span>
                      </div>
                    )}
                    {coverUploading && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white text-sm">Caricamento...</span>
                      </div>
                    )}
                  </div>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleCoverUpload}
                  />
                  <p className="text-xs text-text-secondary mt-1">Dimensione consigliata: 1200×400px</p>
                </div>

                {/* Logo */}
                <div>
                  <p className="text-sm font-medium mb-2">Logo</p>
                  <div className="flex items-center gap-4">
                    <div
                      className="w-24 h-24 rounded-xl overflow-hidden border-2 border-dashed border-border bg-gray-50 cursor-pointer hover:border-primary transition-colors flex items-center justify-center"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      {(logoPreview || dashboard?.producer?.logo_url) ? (
                        <img
                          src={logoPreview || dashboard.producer.logo_url}
                          alt="Logo"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Upload className="w-6 h-6 text-text-secondary" />
                      )}
                      {logoUploading && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-xl">
                          <span className="text-white text-xs">...</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        className="text-sm text-primary hover:underline"
                      >
                        {dashboard?.producer?.logo_url ? 'Cambia logo' : 'Carica logo'}
                      </button>
                      <p className="text-xs text-text-secondary mt-1">JPG, PNG, WebP — max 5MB</p>
                      <p className="text-xs text-text-secondary">Consigliato: 400×400px</p>
                    </div>
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                </div>
              </div>

              {/* Form dati vetrina */}
              <div className="bg-white rounded-xl border border-border p-6 space-y-5">
                <h2 className="font-heading text-xl font-bold">Descrizione e storia</h2>

                <div>
                  <label className="block text-sm font-medium mb-1">Descrizione breve</label>
                  <textarea
                    rows={3}
                    value={storefrontForm.description}
                    onChange={(e) => setStorefrontForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Presentati in poche righe..."
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">La tua storia</label>
                  <textarea
                    rows={5}
                    value={storefrontForm.story}
                    onChange={(e) => setStorefrontForm(f => ({ ...f, story: e.target.value }))}
                    placeholder="Racconta come è nata la tua attività, la tua filosofia..."
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Orari di apertura</label>
                  <textarea
                    rows={3}
                    value={storefrontForm.business_hours}
                    onChange={(e) => setStorefrontForm(f => ({ ...f, business_hours: e.target.value }))}
                    placeholder={"Lun–Ven: 9:00–18:00\nSab: 9:00–13:00\nDom: chiuso"}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none font-mono"
                  />
                </div>
              </div>

              {/* Contatti e social */}
              <div className="bg-white rounded-xl border border-border p-6 space-y-5">
                <h2 className="font-heading text-xl font-bold">Contatti e social</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Sito web</label>
                    <div className="flex items-center border border-border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary/30">
                      <Globe className="w-4 h-4 ml-3 text-text-secondary flex-shrink-0" />
                      <input
                        type="url"
                        value={storefrontForm.website}
                        onChange={(e) => setStorefrontForm(f => ({ ...f, website: e.target.value }))}
                        placeholder="https://www.miosito.it"
                        className="flex-1 px-3 py-2 text-sm focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Telefono</label>
                    <div className="flex items-center border border-border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary/30">
                      <Phone className="w-4 h-4 ml-3 text-text-secondary flex-shrink-0" />
                      <input
                        type="tel"
                        value={storefrontForm.phone}
                        onChange={(e) => setStorefrontForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder="+39 000 000 0000"
                        className="flex-1 px-3 py-2 text-sm focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Instagram</label>
                    <div className="flex items-center border border-border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary/30">
                      <AtSign className="w-4 h-4 ml-3 text-text-secondary flex-shrink-0" />
                      <input
                        type="url"
                        value={storefrontForm.instagram}
                        onChange={(e) => setStorefrontForm(f => ({ ...f, instagram: e.target.value }))}
                        placeholder="https://instagram.com/nomeutente"
                        className="flex-1 px-3 py-2 text-sm focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Facebook</label>
                    <div className="flex items-center border border-border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary/30">
                      <Users className="w-4 h-4 ml-3 text-text-secondary flex-shrink-0" />
                      <input
                        type="url"
                        value={storefrontForm.facebook}
                        onChange={(e) => setStorefrontForm(f => ({ ...f, facebook: e.target.value }))}
                        placeholder="https://facebook.com/nomepagina"
                        className="flex-1 px-3 py-2 text-sm focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Indirizzo */}
                <div>
                  <label className="block text-sm font-medium mb-1">Indirizzo</label>
                  <input
                    type="text"
                    value={storefrontForm.address}
                    onChange={(e) => setStorefrontForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="Via Roma 1"
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Città</label>
                    <input
                      type="text"
                      value={storefrontForm.city}
                      onChange={(e) => setStorefrontForm(f => ({ ...f, city: e.target.value }))}
                      placeholder="Napoli"
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">CAP</label>
                    <input
                      type="text"
                      value={storefrontForm.postal_code}
                      onChange={(e) => setStorefrontForm(f => ({ ...f, postal_code: e.target.value }))}
                      placeholder="80100"
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleStorefrontSave}
                    disabled={storefrontSaving}
                    className="px-6 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {storefrontSaving ? 'Salvataggio...' : 'Salva vetrina'}
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="overview">
            <div className="bg-white rounded-xl border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold">I Tuoi Prodotti</h2>
                {isApproved && (
                  <Dialog open={productDialogOpen} onOpenChange={(open) => {
                    setProductDialogOpen(open);
                    if (!open) {
                      setEditingProduct(null);
                      resetProductForm();
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="gap-2" data-testid="add-product-btn">
                        <Plus className="w-4 h-4" />
                        Aggiungi Prodotto
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>
                          {editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleProductSubmit} className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Nome *</Label>
                            <Input
                              value={productForm.name}
                              onChange={(e) => setProductForm({
                                ...productForm,
                                name: e.target.value,
                                slug: editingProduct ? productForm.slug : generateSlug(e.target.value)
                              })}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Slug *</Label>
                            <Input
                              value={productForm.slug}
                              onChange={(e) => setProductForm({ ...productForm, slug: e.target.value })}
                              required
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Categoria</Label>
                          <Select value={productForm.category_id || "none"} onValueChange={(v) => setProductForm({ ...productForm, category_id: v === "none" ? "" : v })}>
                            <SelectTrigger className="mt-1" data-testid="category-select">
                              <SelectValue placeholder="Seleziona categoria" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Seleziona una categoria...</SelectItem>
                              <SelectSeparator />
                              
                              {/* Raggruppa le categorie per macro_type */}
                              {(() => {
                                // Raggruppa categorie per macro_type
                                const grouped = categories.reduce((acc, cat) => {
                                  const mt = cat.macro_type || 'FOOD';
                                  if (!acc[mt]) acc[mt] = [];
                                  acc[mt].push(cat);
                                  return acc;
                                }, {});
                                
                                const macroTypeLabels = {
                                  FOOD: { label: 'Prodotti Alimentari', icon: '🍝' },
                                  ARTIGIANATO: { label: 'Artigianato', icon: '🎨' },
                                  EXPERIENCE: { label: 'Esperienze', icon: '✨' }
                                };
                                
                                const orderedMacroTypes = ['FOOD', 'ARTIGIANATO', 'EXPERIENCE'];
                                
                                return orderedMacroTypes.map(mt => {
                                  const cats = grouped[mt];
                                  if (!cats || cats.length === 0) return null;
                                  
                                  const info = macroTypeLabels[mt] || { label: mt, icon: '📦' };
                                  
                                  return (
                                    <SelectGroup key={mt}>
                                      <SelectLabel className="text-xs font-bold text-text-muted bg-muted/50 py-2 px-2 -mx-1">
                                        {info.icon} {info.label}
                                      </SelectLabel>
                                      {cats.map(cat => (
                                        <SelectItem key={cat.id} value={cat.id} className="pl-4">
                                          {cat.name}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  );
                                });
                              })()}
                            </SelectContent>
                          </Select>
                          {/* Mostra info macro_type selezionato */}
                          {productForm.category_id && (() => {
                            const selectedCat = categories.find(c => c.id === productForm.category_id);
                            if (selectedCat?.macro_type === 'EXPERIENCE') {
                              return (
                                <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" />
                                  Esperienza: richiede data evento e luogo
                                </p>
                              );
                            }
                            if (selectedCat?.macro_type === 'FOOD') {
                              return (
                                <p className="text-xs text-orange-600 mt-1">
                                  Prodotto alimentare: recesso 14 giorni
                                </p>
                              );
                            }
                            if (selectedCat?.macro_type === 'ARTIGIANATO') {
                              return (
                                <p className="text-xs text-blue-600 mt-1">
                                  Artigianato: finestra reso 14 giorni
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div>
                          <Label>Descrizione</Label>
                          <Textarea
                            value={productForm.description}
                            onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                            rows={3}
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Prezzo * (€)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={productForm.price}
                              onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Prezzo Originale (€)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={productForm.compare_price}
                              onChange={(e) => setProductForm({ ...productForm, compare_price: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Inventario *</Label>
                            <Input
                              type="number"
                              min="0"
                              value={productForm.inventory}
                              onChange={(e) => setProductForm({ ...productForm, inventory: e.target.value })}
                              required
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Immagine prodotto</Label>
                          <div
                            className="mt-1 relative w-full h-36 rounded-lg overflow-hidden border-2 border-dashed border-border bg-gray-50 cursor-pointer hover:border-primary transition-colors flex items-center justify-center"
                            onClick={() => productImageRef.current?.click()}
                          >
                            {productImagePreview ? (
                              <img
                                src={productImagePreview}
                                alt="Anteprima"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex flex-col items-center text-text-secondary">
                                <Upload className="w-6 h-6 mb-1" />
                                <span className="text-xs">Carica immagine</span>
                                <span className="text-xs">JPG, PNG, WebP — max 5MB</span>
                              </div>
                            )}
                            {productImagePreview && (
                              <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center">
                                <span className="text-white text-xs opacity-0 hover:opacity-100">Cambia</span>
                              </div>
                            )}
                          </div>
                          <input
                            ref={productImageRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setProductImageFile(file);
                              setProductImagePreview(URL.createObjectURL(file));
                            }}
                          />
                          {productImagePreview && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setProductImageFile(null); setProductImagePreview(null); }}
                              className="mt-1 text-xs text-red-500 hover:underline"
                            >
                              Rimuovi immagine
                            </button>
                          )}
                        </div>
                        
                        {/* Campi EXPERIENCE - visibili solo per categorie EXPERIENCE */}
                        {productForm.category_id && categories.find(c => c.id === productForm.category_id)?.macro_type === 'EXPERIENCE' && (
                          <div className="border border-purple-200 bg-purple-50 rounded-lg p-4 space-y-4">
                            <h4 className="font-medium text-purple-800 flex items-center gap-2">
                              <Sparkles className="w-4 h-4" />
                              Dettagli Esperienza
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-purple-700">Data Evento *</Label>
                                <Input
                                  type="datetime-local"
                                  value={productForm.event_date}
                                  onChange={(e) => setProductForm({ ...productForm, event_date: e.target.value })}
                                  required
                                  className="mt-1"
                                  data-testid="event-date-input"
                                />
                                <p className="text-xs text-purple-600 mt-1">
                                  La policy di rimborso sarà calcolata in base a questa data
                                </p>
                              </div>
                              <div>
                                <Label className="text-purple-700">Luogo *</Label>
                                <Input
                                  value={productForm.location}
                                  onChange={(e) => setProductForm({ ...productForm, location: e.target.value })}
                                  placeholder="Es: Napoli, Via Toledo 123"
                                  required
                                  className="mt-1"
                                  data-testid="location-input"
                                />
                              </div>
                            </div>
                            <div className="bg-purple-100 rounded p-3 text-sm text-purple-800">
                              <p className="font-medium mb-1">Policy di rimborso per Esperienze:</p>
                              <ul className="list-disc list-inside text-xs space-y-1">
                                <li>≥7 giorni prima dell'evento: rimborso 100%</li>
                                <li>3-7 giorni prima: rimborso 50%</li>
                                <li>&lt;3 giorni prima: nessun rimborso</li>
                              </ul>
                            </div>
                          </div>
                        )}
                        
                        <div>
                          <Label>Stato</Label>
                          <Select value={productForm.status} onValueChange={(v) => setProductForm({ ...productForm, status: v })}>
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="draft">Bozza</SelectItem>
                              <SelectItem value="active">Attivo</SelectItem>
                              <SelectItem value="inactive">Inattivo</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2 pt-4">
                          <Button type="submit" className="flex-1">
                            {editingProduct ? 'Salva Modifiche' : 'Crea Prodotto'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {products.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 mx-auto text-text-muted mb-4" />
                  <p className="text-text-secondary">Nessun prodotto ancora</p>
                  {!isApproved && (
                    <p className="text-sm text-text-muted mt-2">
                      Potrai aggiungere prodotti dopo l'approvazione
                    </p>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Prodotto</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Prezzo</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Stock</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Stato</th>
                        <th className="text-right py-3 px-2 text-sm font-medium text-text-muted">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map(product => (
                        <tr key={product.id} className="border-b border-border last:border-0">
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-3">
                              <img
                                src={product.image_url || 'https://images.pexels.com/photos/7663192/pexels-photo-7663192.jpeg'}
                                alt={product.name}
                                className="w-10 h-10 rounded-lg object-cover"
                              />
                              <span className="font-medium truncate max-w-[200px]">{product.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2">{formatPrice(product.price)}</td>
                          <td className="py-3 px-2">{product.inventory}</td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              product.status === 'active' ? 'bg-secondary/10 text-secondary' :
                              product.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {product.status === 'active' ? 'Attivo' : product.status === 'draft' ? 'Bozza' : 'Inattivo'}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(product)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteProduct(product.id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Shipping Tab */}
          <TabsContent value="shipping">
            <div className="bg-white rounded-xl border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                    <Truck className="w-5 h-5" />
                    Gestione Spedizioni
                  </h2>
                  <p className="text-sm text-text-muted mt-1">
                    Tu sei il mittente di tutte le spedizioni. DalSud gestisce solo la piattaforma.
                  </p>
                </div>
                <Button variant="outline" onClick={fetchShippingOrders} size="sm">
                  Aggiorna
                </Button>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Come funziona la spedizione</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700">
                      <li>Genera l'etichetta o inserisci un tracking manuale</li>
                      <li>Stampa l'etichetta e applica al pacco</li>
                      <li>Consegna il pacco al corriere</li>
                      <li>Conferma la spedizione dalla dashboard</li>
                    </ul>
                  </div>
                </div>
              </div>

              {shippingLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : shippingOrders.length === 0 ? (
                <div className="text-center py-12">
                  <Truck className="w-12 h-12 mx-auto text-text-muted mb-4" />
                  <p className="text-text-secondary">Nessun ordine da spedire</p>
                  <p className="text-sm text-text-muted mt-2">
                    Gli ordini pagati appariranno qui
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {shippingOrders.map(orderData => (
                    <ShippingOrderCard 
                      key={orderData.order.id} 
                      orderData={orderData}
                      onRefresh={fetchShippingOrders}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Returns Tab */}
          <TabsContent value="returns">
            <div className="bg-white rounded-xl border border-border p-6">
              <div className="mb-6">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                  <RotateCcw className="w-5 h-5" />
                  Richieste di Reso
                </h2>
                <p className="text-sm text-text-muted mt-1">
                  Gestisci le richieste di reso dei tuoi clienti. Ricorda: hai l'obbligo di rispondere 
                  entro termini ragionevoli secondo la normativa UE.
                </p>
              </div>

              {/* Info box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Diritto di recesso UE - 14 giorni</p>
                    <p>I clienti hanno diritto di recedere dall'acquisto entro 14 giorni dalla consegna, 
                    senza obbligo di motivazione. Tu sei responsabile della gestione del reso e del rimborso.</p>
                  </div>
                </div>
              </div>

              {returnsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : returnRequests.length === 0 ? (
                <div className="text-center py-12">
                  <RotateCcw className="w-12 h-12 mx-auto text-text-muted mb-4" />
                  <p className="text-text-secondary">Nessuna richiesta di reso</p>
                  <p className="text-sm text-text-muted mt-2">
                    Le richieste di reso dei clienti appariranno qui
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {returnRequests.map(returnData => (
                    <ReturnRequestCard 
                      key={returnData.id} 
                      returnData={returnData}
                      onRefresh={fetchReturnRequests}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-white rounded-xl border border-border p-6">
              <h2 className="font-heading text-xl font-bold mb-6">Ordini Recenti</h2>
              {recent_orders.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="w-12 h-12 mx-auto text-text-muted mb-4" />
                  <p className="text-text-secondary">Nessun ordine ancora</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recent_orders.map(order => (
                    <div key={order.id} className="flex items-center justify-between p-4 bg-background rounded-lg">
                      <div>
                        <p className="font-mono text-sm">{order.order_number}</p>
                        <p className="text-xs text-text-muted">
                          {new Date(order.created_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatPrice(order.total)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          order.status === 'paid' ? 'bg-secondary/10 text-secondary' :
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Ledger / Saldo Tab */}
          <TabsContent value="ledger">
            <LedgerTab />
          </TabsContent>

          {/* Shipping Settings Tab */}
          <TabsContent value="shipping-settings">
            <div className="bg-white rounded-xl border border-border p-6">
              <div className="mb-6">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Impostazioni Costi Spedizione
                </h2>
                <p className="text-sm text-text-muted mt-1">
                  Configura come calcolare i costi di spedizione per i tuoi prodotti.
                </p>
              </div>

              <form onSubmit={handleSaveShippingSettings} className="space-y-6">
                {/* Tipo di costo */}
                <div>
                  <Label className="text-sm font-medium">Tipo di Calcolo Spedizione</Label>
                  <Select 
                    value={shippingForm.shipping_cost_type} 
                    onValueChange={(v) => setShippingForm({ ...shippingForm, shipping_cost_type: v })}
                  >
                    <SelectTrigger className="mt-2" data-testid="shipping-cost-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Spedizione Gratuita
                        </span>
                      </SelectItem>
                      <SelectItem value="flat_rate">
                        <span className="flex items-center gap-2">
                          <Euro className="w-4 h-4" />
                          Tariffa Fissa
                        </span>
                      </SelectItem>
                      <SelectItem value="by_weight">
                        <span className="flex items-center gap-2">
                          <Scale className="w-4 h-4" />
                          Per Peso (€/kg)
                        </span>
                      </SelectItem>
                      <SelectItem value="by_quantity">
                        <span className="flex items-center gap-2">
                          <Box className="w-4 h-4" />
                          Per Quantità Articoli
                        </span>
                      </SelectItem>
                      <SelectItem value="combined">
                        <span className="flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Combinato (Base + Peso + Quantità)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Cost inputs based on type */}
                {shippingForm.shipping_cost_type !== 'free' && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Flat Rate - always shown for non-free */}
                    {['flat_rate', 'by_quantity', 'combined'].includes(shippingForm.shipping_cost_type) && (
                      <div>
                        <Label className="text-sm">
                          <Euro className="w-3 h-3 inline mr-1" />
                          Tariffa Base (€)
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={shippingForm.shipping_flat_rate}
                          onChange={(e) => setShippingForm({ ...shippingForm, shipping_flat_rate: e.target.value })}
                          placeholder="es. 5.00"
                          className="mt-1"
                          data-testid="shipping-flat-rate"
                        />
                        <p className="text-xs text-text-muted mt-1">Costo base per ogni ordine</p>
                      </div>
                    )}

                    {/* Per KG */}
                    {['by_weight', 'combined'].includes(shippingForm.shipping_cost_type) && (
                      <div>
                        <Label className="text-sm">
                          <Scale className="w-3 h-3 inline mr-1" />
                          Costo per Kg (€)
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={shippingForm.shipping_per_kg}
                          onChange={(e) => setShippingForm({ ...shippingForm, shipping_per_kg: e.target.value })}
                          placeholder="es. 1.50"
                          className="mt-1"
                          data-testid="shipping-per-kg"
                        />
                        <p className="text-xs text-text-muted mt-1">Costo aggiuntivo per ogni kg</p>
                      </div>
                    )}

                    {/* Per Item */}
                    {['by_quantity', 'combined'].includes(shippingForm.shipping_cost_type) && (
                      <div>
                        <Label className="text-sm">
                          <Box className="w-3 h-3 inline mr-1" />
                          Costo per Articolo Extra (€)
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={shippingForm.shipping_per_item}
                          onChange={(e) => setShippingForm({ ...shippingForm, shipping_per_item: e.target.value })}
                          placeholder="es. 0.50"
                          className="mt-1"
                          data-testid="shipping-per-item"
                        />
                        <p className="text-xs text-text-muted mt-1">Costo per ogni articolo oltre il primo</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Free Shipping Threshold */}
                <div className="border-t border-border pt-4">
                  <Label className="text-sm font-medium">
                    Soglia Spedizione Gratuita (opzionale)
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={shippingForm.shipping_free_threshold}
                    onChange={(e) => setShippingForm({ ...shippingForm, shipping_free_threshold: e.target.value })}
                    placeholder="es. 50.00"
                    className="mt-2 max-w-xs"
                    data-testid="shipping-free-threshold"
                  />
                  <p className="text-xs text-text-muted mt-1">
                    Se il cliente supera questa soglia, la spedizione diventa gratuita. Lascia 0 per disabilitare.
                  </p>
                </div>

                {/* Summary */}
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="text-sm font-medium mb-2">Riepilogo Impostazioni</h4>
                  <div className="text-sm text-text-secondary space-y-1">
                    {shippingForm.shipping_cost_type === 'free' && (
                      <p>✓ Spedizione sempre gratuita per tutti gli ordini</p>
                    )}
                    {shippingForm.shipping_cost_type === 'flat_rate' && (
                      <p>✓ Tariffa fissa: €{shippingForm.shipping_flat_rate || 0} per ordine</p>
                    )}
                    {shippingForm.shipping_cost_type === 'by_weight' && (
                      <p>✓ €{shippingForm.shipping_per_kg || 0} per ogni kg di peso</p>
                    )}
                    {shippingForm.shipping_cost_type === 'by_quantity' && (
                      <>
                        <p>✓ Base: €{shippingForm.shipping_flat_rate || 0}</p>
                        <p>✓ +€{shippingForm.shipping_per_item || 0} per ogni articolo aggiuntivo</p>
                      </>
                    )}
                    {shippingForm.shipping_cost_type === 'combined' && (
                      <>
                        <p>✓ Base: €{shippingForm.shipping_flat_rate || 0}</p>
                        <p>✓ +€{shippingForm.shipping_per_kg || 0}/kg</p>
                        <p>✓ +€{shippingForm.shipping_per_item || 0} per articolo extra</p>
                      </>
                    )}
                    {parseFloat(shippingForm.shipping_free_threshold) > 0 && (
                      <p className="text-green-700">
                        ✓ Gratis per ordini sopra €{shippingForm.shipping_free_threshold}
                      </p>
                    )}
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={settingsLoading}
                  className="gap-2"
                  data-testid="save-shipping-settings-btn"
                >
                  {settingsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Salva Impostazioni
                </Button>
              </form>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="space-y-6">

              {/* ── Stripe Connect Section ── */}
              {stripeConnectLoading ? (
                <div className="bg-white rounded-xl border border-border p-4 flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span className="text-sm text-text-muted">Caricamento stato Stripe...</span>
                </div>
              ) : stripeConnect && !stripeConnect.connected ? (
                /* Stato 1 — Non collegato */
                <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <CreditCard className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base mb-1">Collega il tuo account Stripe</h3>
                      <p className="text-sm text-text-muted mb-4">
                        Collega il tuo account Stripe per ricevere i pagamenti direttamente sul tuo conto.
                      </p>
                      <Button
                        onClick={() => handleStripeOnboarding(producer.id)}
                        disabled={stripeConnectWorking}
                        className="gap-2"
                      >
                        {stripeConnectWorking ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ExternalLink className="w-4 h-4" />
                        )}
                        Collega Stripe
                      </Button>
                    </div>
                  </div>
                </div>
              ) : stripeConnect && stripeConnect.connected && !stripeConnect.onboarding_completed ? (
                /* Stato 2 — Onboarding incompleto */
                <div className="bg-yellow-50 rounded-xl border border-yellow-300 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base mb-1 text-yellow-800">Onboarding Stripe incompleto</h3>
                      <p className="text-sm text-yellow-700 mb-4">
                        Completa la configurazione Stripe per ricevere i pagamenti.
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          onClick={() => handleStripeOnboarding(producer.id)}
                          disabled={stripeConnectWorking}
                          className="gap-2 bg-yellow-600 hover:bg-yellow-700"
                        >
                          {stripeConnectWorking ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <ExternalLink className="w-4 h-4" />
                          )}
                          Completa onboarding
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleStripeConnectSync(producer.id)}
                          disabled={stripeConnectWorking}
                          className="gap-2 border-yellow-400 text-yellow-700 hover:bg-yellow-100"
                        >
                          {stripeConnectWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          Sincronizza stato
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : stripeConnect && stripeConnect.connected && stripeConnect.onboarding_completed && !stripeConnect.payouts_enabled ? (
                /* Stato 3 — In verifica */
                <div className="bg-orange-50 rounded-xl border border-orange-300 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center shrink-0">
                      <Clock className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base mb-1 text-orange-800">Verifica in corso</h3>
                      <p className="text-sm text-orange-700">
                        Stripe sta verificando il tuo account. I pagamenti verranno abilitati appena la verifica sarà completata.
                        Di solito richiede 1-2 giorni lavorativi.
                      </p>
                    </div>
                  </div>
                </div>
              ) : stripeConnect && stripeConnect.payouts_enabled ? (
                /* Stato 4 — Attivo */
                <div className="bg-green-50 rounded-xl border border-green-300 p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                    <div>
                      <span className="font-medium text-green-800">Account Stripe collegato correttamente</span>
                      <span className="text-xs text-green-600 ml-2">{stripeConnect.stripe_account_id}</span>
                    </div>
                  </div>
                </div>
              ) : null}
              {/* ── Fine Stripe Connect Section ── */}

              {paymentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : paymentsDashboard ? (
                <>
                  {/* Info Banner */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-blue-800">
                          {paymentsDashboard.messages?.payout_info}
                        </p>
                        {paymentsDashboard.messages?.blocked_info && (
                          <p className="text-sm text-red-700 mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            {paymentsDashboard.messages.blocked_info}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Prossimo Payout */}
                    <div className="bg-white rounded-xl border border-border p-4">
                      <div className="flex items-center gap-2 text-text-muted mb-2">
                        <Calendar className="w-4 h-4" />
                        <span className="text-xs">Prossimo Payout</span>
                      </div>
                      <p className="text-2xl font-bold text-primary">
                        {paymentsDashboard.period?.next_payout_date}
                      </p>
                      <p className="text-xs text-text-muted mt-1">
                        Periodo: {paymentsDashboard.period?.start?.slice(0,10)} - {paymentsDashboard.period?.end?.slice(0,10)}
                      </p>
                    </div>

                    {/* Pronto per Payout */}
                    <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                      <div className="flex items-center gap-2 text-green-700 mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs">Pronto per Payout</span>
                      </div>
                      <p className="text-2xl font-bold text-green-700">
                        €{(
                          (paymentsDashboard.current_period?.net_total || 0) +
                          (paymentsDashboard.current_period?.shipping_total || 0)
                        ).toFixed(2)}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        {paymentsDashboard.current_period?.orders_ready} ordini idonei
                      </p>
                      <div className="text-[10px] text-green-500 mt-1 space-y-0.5">
                        <p>Prodotti: €{paymentsDashboard.current_period?.gross_total?.toFixed(2)}</p>
                        <p>Spedizione: +€{(paymentsDashboard.current_period?.shipping_total || 0).toFixed(2)}</p>
                      </div>
                    </div>

                    {/* In Attesa (Recesso) */}
                    <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4">
                      <div className="flex items-center gap-2 text-yellow-700 mb-2">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs">In Attesa (Recesso)</span>
                      </div>
                      <p className="text-2xl font-bold text-yellow-700">
                        €{(
                          (paymentsDashboard.pending?.net_total || 0) +
                          (paymentsDashboard.pending?.shipping_total || 0)
                        ).toFixed(2)}
                      </p>
                      <p className="text-xs text-yellow-600 mt-1">
                        {paymentsDashboard.pending?.orders_count} ordini in finestra recesso
                      </p>
                      <div className="text-[10px] text-yellow-500 mt-1 space-y-0.5">
                        <p>Prodotti: €{paymentsDashboard.pending?.gross_total?.toFixed(2)}</p>
                        <p>Spedizione: +€{(paymentsDashboard.pending?.shipping_total || 0).toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Bloccati */}
                    <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                      <div className="flex items-center gap-2 text-red-700 mb-2">
                        <Ban className="w-4 h-4" />
                        <span className="text-xs">Bloccati (Tuoi Prodotti)</span>
                      </div>
                      <p className="text-2xl font-bold text-red-700">
                        {paymentsDashboard.blocked?.orders_count}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Ordini con reso/contestazione
                      </p>
                      <p className="text-[10px] text-red-500 mt-1 italic">
                        Solo ordini con resi sui tuoi prodotti
                      </p>
                    </div>
                  </div>

                  {/* Commission Info - Sezione Didattica con Spedizione */}
                  <div className="bg-white rounded-xl border border-border p-4">
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <CircleDollarSign className="w-5 h-5 text-primary" />
                      Dettaglio Payout Periodo Corrente
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <p className="text-text-muted">Prodotti (Base)</p>
                        <p className="font-semibold">€{paymentsDashboard.current_period?.gross_total?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-text-muted">Commissione DalSud ({(paymentsDashboard.commission_rate * 100).toFixed(0)}%)</p>
                        <p className="font-semibold text-red-600">-€{paymentsDashboard.current_period?.commission_total?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-text-muted">Netto Prodotti (IVA esclusa)</p>
                        <p className="font-bold text-green-600">
                          €{(paymentsDashboard.current_period?.net_total || 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-2">
                        <p className="text-text-muted text-xs flex items-center gap-1">
                          <Truck className="w-3 h-3" />
                          Rimborso Spedizione
                        </p>
                        <p className="font-semibold text-blue-600">+€{(paymentsDashboard.current_period?.shipping_total || 0).toFixed(2)}</p>
                        <p className="text-[10px] text-blue-500 italic">100% rimborsata</p>
                      </div>
                      <div className="bg-green-100 rounded-lg p-2">
                        <p className="text-text-muted text-xs font-medium">TOTALE PAYOUT</p>
                        <p className="font-bold text-lg text-green-700">
                          €{(
                            (paymentsDashboard.current_period?.net_total || 0) +
                            (paymentsDashboard.current_period?.shipping_total || 0)
                          ).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-700">
                          <strong>Spedizione:</strong> La spedizione è anticipata da te e viene rimborsata al 100%. 
                          Non è soggetta a commissione DalSud.
                        </p>
                      </div>
                      <div className="p-2 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">
                          <strong>IVA su Commissione ({(paymentsDashboard.vat_rate * 100).toFixed(0)}%):</strong> €{paymentsDashboard.current_period?.vat_total?.toFixed(2)} - 
                          Solo se in regime IVA, sottrarre dal netto.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Orders List */}
                  <div className="bg-white rounded-xl border border-border p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5" />
                        Ordini con Dettaglio Economico
                      </h3>
                      <Select value={paymentsFilter || "all"} onValueChange={(val) => handlePaymentsFilterChange(val === "all" ? "" : val)}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Tutti gli stati" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti gli stati</SelectItem>
                          <SelectItem value="in_attesa">In Attesa</SelectItem>
                          <SelectItem value="idoneo_payout">Idoneo Payout</SelectItem>
                          <SelectItem value="bloccato">Bloccato</SelectItem>
                          <SelectItem value="pagato">Pagato</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {paymentsOrders.items?.length === 0 ? (
                      <div className="text-center py-8 text-text-muted">
                        <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Nessun ordine trovato</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {paymentsOrders.items?.map((order) => (
                          <div
                            key={order.id}
                            className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${order.active_dispute ? 'border-red-400' : 'border-border'}`}
                          >
                            {order.active_dispute && (
                              <div className="mb-3 bg-red-50 rounded px-3 py-1.5 flex items-center gap-2 text-red-700 text-xs font-medium">
                                <span>⚠️</span>
                                <span>
                                  Chargeback aperto — {order.active_dispute.reason?.replace(/_/g, ' ')} · €{order.active_dispute.amount?.toFixed(2)}
                                  {order.active_dispute.due_by && (
                                    <> · Scadenza: {new Date(order.active_dispute.due_by).toLocaleDateString('it-IT')}</>
                                  )}
                                </span>
                              </div>
                            )}
                            <div className="flex flex-wrap items-center justify-between gap-4">
                              <div>
                                <p className="font-medium">{order.order_number}</p>
                                <p className="text-xs text-text-muted">
                                  {order.created_at ? new Date(order.created_at).toLocaleDateString('it-IT') : ''}
                                  {order.delivered_at && ` • Consegnato: ${new Date(order.delivered_at).toLocaleDateString('it-IT')}`}
                                </p>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Badge 
                                  variant="outline" 
                                  className={`
                                    ${order.payment_status === 'idoneo_payout' ? 'bg-green-100 text-green-700 border-green-300' : ''}
                                    ${order.payment_status === 'in_attesa' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' : ''}
                                    ${order.payment_status === 'bloccato' ? 'bg-red-100 text-red-700 border-red-300' : ''}
                                    ${order.payment_status === 'pagato' ? 'bg-blue-100 text-blue-700 border-blue-300' : ''}
                                  `}
                                >
                                  {order.payment_status_display?.label}
                                </Badge>
                                {(order.days_to_eligible !== null && order.days_to_eligible !== undefined) ? (
                                  <span 
                                    className="text-xs text-yellow-600 flex items-center gap-1"
                                    title={
                                      order.macro_type === 'EXPERIENCE'
                                        ? `Idoneo al payout 3 giorni prima dell'evento${order.event_date ? ` (${new Date(order.event_date).toLocaleDateString('it-IT')})` : ''}`
                                        : 'Idoneo al payout dopo 14 giorni dalla consegna'
                                    }
                                  >
                                    <Clock className="w-3 h-3" />
                                    {order.macro_type === 'EXPERIENCE'
                                      ? `${order.days_to_eligible}gg all'evento`
                                      : `${order.days_to_eligible}gg`
                                    }
                                  </span>
                                ) : null}
                                {/* Badge tipo prodotto per EXPERIENCE */}
                                {order.macro_type === 'EXPERIENCE' && (
                                  <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300 text-xs">
                                    <Sparkles className="w-3 h-3 mr-1" />
                                    Esperienza
                                  </Badge>
                                )}
                              </div>
                              
                              <div className="flex flex-wrap items-center gap-2 text-sm">
                                <div className="text-right min-w-[70px]">
                                  <p className="text-text-muted text-xs">Prodotti</p>
                                  <p className="font-medium">€{order.economics?.gross_amount?.toFixed(2)}</p>
                                </div>
                                <div className="text-right min-w-[70px]">
                                  <p className="text-text-muted text-xs flex items-center gap-1 justify-end">
                                    <Truck className="w-3 h-3" />
                                    Sped.
                                  </p>
                                  <p className="font-medium text-blue-600">+€{(order.economics?.shipping_cost || 0).toFixed(2)}</p>
                                </div>
                                <div className="text-right min-w-[70px]">
                                  <p className="text-text-muted text-xs">Comm.</p>
                                  <p className="font-medium text-red-600">-€{order.economics?.commission_amount?.toFixed(2)}</p>
                                </div>
                                {order.economics?.refund_deducted > 0 && (
                                  <div className="text-right min-w-[70px]">
                                    <p className="text-text-muted text-xs">Rimborso</p>
                                    <p className="font-medium text-orange-600">-€{order.economics.refund_deducted.toFixed(2)}</p>
                                  </div>
                                )}
                                <div className={`text-right min-w-[90px] rounded px-2 py-1 ${order.economics?.refund_deducted > 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
                                  <p className="text-text-muted text-xs">Totale Payout</p>
                                  <p className={`font-bold ${order.economics?.refund_deducted > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                    €{(order.economics?.net_payout ?? order.economics?.total_payout ?? ((order.economics?.gross_amount || 0) - (order.economics?.commission_amount || 0) + (order.economics?.shipping_cost || 0))).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {order.payment_status === 'bloccato' && (
                              <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Ordine bloccato: reso o contestazione sui tuoi prodotti
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Payout History */}
                  <div className="bg-white rounded-xl border border-border p-6">
                    <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Storico Pagamenti Ricevuti
                    </h3>
                    
                    {payoutsHistory?.length === 0 ? (
                      <div className="text-center py-8 text-text-muted">
                        <CircleDollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Nessun pagamento ricevuto ancora</p>
                        <p className="text-sm mt-1">I pagamenti appariranno qui dopo il primo payout quindicinale</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-2">Periodo</th>
                              <th className="text-right py-2 px-2">Ordini</th>
                              <th className="text-right py-2 px-2">Prodotti</th>
                              <th className="text-right py-2 px-2">Spedizione</th>
                              <th className="text-right py-2 px-2">Comm.</th>
                              <th className="text-right py-2 px-2 bg-green-50">Totale Payout</th>
                              <th className="text-left py-2 px-2">Data</th>
                              <th className="text-left py-2 px-2">Rif.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payoutsHistory.map((payout) => (
                              <tr key={payout.id} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-2">
                                  {payout.period_start} - {payout.period_end}
                                </td>
                                <td className="py-2 px-2 text-right">{payout.orders_count}</td>
                                <td className="py-2 px-2 text-right">€{payout.gross_amount?.toFixed(2)}</td>
                                <td className="py-2 px-2 text-right text-blue-600">
                                  +€{(payout.shipping_refund || 0).toFixed(2)}
                                </td>
                                <td className="py-2 px-2 text-right text-red-600">
                                  -€{payout.commission_total?.toFixed(2)}
                                </td>
                                <td className="py-2 px-2 text-right font-bold text-green-600 bg-green-50">
                                  €{payout.net_amount?.toFixed(2)}
                                </td>
                                <td className="py-2 px-2">{payout.processed_at}</td>
                                <td className="py-2 px-2 font-mono text-xs">{payout.payment_reference}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <p className="text-xs text-text-muted mt-2 italic">
                          * Il Totale Payout include Netto Prodotti + Rimborso Spedizione. Se in regime IVA, contattare commercialista per IVA commissioni.
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Rimborsi Resi */}
                  {vendorRefunds && (
                    <div className="bg-white rounded-xl border border-border p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                          <RotateCcw className="w-5 h-5 text-orange-500" />
                          Rimborsi Resi Emessi
                        </h3>
                        {vendorRefunds.total_refunded_eur > 0 && (
                          <span className="text-sm font-semibold text-orange-600">
                            Totale rimborsato: €{vendorRefunds.total_refunded_eur?.toFixed(2)}
                          </span>
                        )}
                      </div>

                      {vendorRefunds.items?.length === 0 ? (
                        <div className="text-center py-6 text-text-muted">
                          <RotateCcw className="w-10 h-10 mx-auto mb-2 opacity-40" />
                          <p className="text-sm">Nessun rimborso emesso</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b text-text-muted">
                                <th className="text-left py-2 px-2">Ordine</th>
                                <th className="text-right py-2 px-2">Importo</th>
                                <th className="text-center py-2 px-2">Stato</th>
                                <th className="text-left py-2 px-2">Note</th>
                                <th className="text-left py-2 px-2">Data</th>
                                <th className="py-2 px-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {vendorRefunds.items.map((r) => (
                                <tr key={r.id} className={`border-b hover:bg-gray-50 ${r.status === 'skipped' ? 'bg-yellow-50/40' : ''}`}>
                                  <td className="py-2 px-2 font-mono text-xs">{r.order_number || r.order_id?.slice(-8)}</td>
                                  <td className="py-2 px-2 text-right">
                                    {r.status === 'skipped' && r.expected_refund_amount ? (
                                      <div>
                                        <span className="text-xs line-through text-gray-400">€{r.expected_refund_amount?.toFixed(2)}</span>
                                        <span className="block text-xs text-gray-500 italic">non processato</span>
                                      </div>
                                    ) : (
                                      <span className="font-semibold text-orange-600">€{r.amount?.toFixed(2)}</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    <Badge className={
                                      r.status === 'succeeded' ? 'bg-green-100 text-green-800' :
                                      r.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      r.status === 'failed' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-700'
                                    }>
                                      {r.status === 'succeeded' ? 'Completato' :
                                       r.status === 'pending' ? 'In corso' :
                                       r.status === 'failed' ? 'Fallito' : 'Saltato'}
                                    </Badge>
                                  </td>
                                  <td className="py-2 px-2 text-xs text-text-muted">
                                    {r.failure_reason || r.notes || '—'}
                                  </td>
                                  <td className="py-2 px-2 text-xs">
                                    {r.created_at ? new Date(r.created_at).toLocaleDateString('it-IT') : '—'}
                                  </td>
                                  <td className="py-2 px-2">
                                    {r.status === 'failed' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-xs h-7 px-2 gap-1 border-orange-300 text-orange-700 hover:bg-orange-50"
                                        disabled={retryingRefundId === r.id}
                                        onClick={() => handleRetryRefund(r.id)}
                                        title={r.stripe_payment_intent_id ? 'Riprova rimborso Stripe' : 'Attenzione: payment intent non disponibile (ordine precedente al fix rimborsi)'}
                                      >
                                        {retryingRefundId === r.id
                                          ? <Loader2 className="w-3 h-3 animate-spin" />
                                          : <RotateCcw className="w-3 h-3" />
                                        }
                                        Riprova
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="text-xs text-text-muted mt-2 italic">
                            I rimborsi vengono detratti automaticamente dal payout del periodo.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-text-muted">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Impossibile caricare i dati dei pagamenti</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <div className="bg-white rounded-xl border border-border p-6">
              <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                <Crown className="w-6 h-6 text-accent" />
                Abbonamento DalSud Pro
              </h2>

              {subscriptionData?.has_subscription ? (
                <div className="space-y-6">
                  {/* Status Card */}
                  <div className={`p-6 rounded-xl border-2 ${
                    subscriptionData.is_pro 
                      ? 'bg-accent/5 border-accent' 
                      : subscriptionData.status === 'incomplete'
                        ? 'bg-yellow-50 border-yellow-300'
                        : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          {subscriptionData.is_pro ? (
                            <CheckCircle className="w-6 h-6 text-accent" />
                          ) : subscriptionData.status === 'incomplete' ? (
                            <RefreshCw className="w-6 h-6 text-yellow-500" />
                          ) : (
                            <AlertTriangle className="w-6 h-6 text-red-500" />
                          )}
                          <span className="text-lg font-semibold">
                            {subscriptionData.is_pro 
                              ? 'Abbonamento Attivo' 
                              : subscriptionData.status === 'incomplete'
                                ? 'In attesa di pagamento'
                                : 'Abbonamento Non Attivo'}
                          </span>
                        </div>
                        <p className="text-text-muted">
                          Stato: <span className="font-medium capitalize">{subscriptionData.status}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-accent">
                          €{subscriptionData.subscription?.amount?.toFixed(2) || '29.90'}
                        </p>
                        <p className="text-sm text-text-muted">/mese</p>
                      </div>
                    </div>
                  </div>

                  {/* Incomplete Status Notice */}
                  {subscriptionData.status === 'incomplete' && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <RefreshCw className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-yellow-800 font-medium">Pagamento in sospeso</p>
                          <p className="text-sm text-yellow-700 mt-1">
                            Il tuo abbonamento sarà attivato automaticamente dopo il pagamento.
                            Se hai già completato il pagamento, attendi qualche secondo oppure usa il pulsante di verifica.
                          </p>
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm"
                              onClick={handleUpgradePlan}
                              className="gap-1 bg-accent hover:bg-accent/90"
                            >
                              <CreditCard className="w-4 h-4" />
                              Completa Pagamento
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={syncSubscriptionWithStripe}
                              disabled={loading}
                              className="gap-1"
                              title="Fallback: sincronizza manualmente lo stato"
                            >
                              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                              Verifica
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Subscription Details */}
                  {subscriptionData.subscription && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-text-muted">Data Inizio</p>
                        <p className="font-medium">
                          {subscriptionData.subscription.start_date 
                            ? new Date(subscriptionData.subscription.start_date).toLocaleDateString('it-IT')
                            : '-'}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-text-muted">Prossimo Rinnovo</p>
                        <p className="font-medium">
                          {subscriptionData.subscription.current_period_end 
                            ? new Date(subscriptionData.subscription.current_period_end).toLocaleDateString('it-IT')
                            : '-'}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-text-muted">Ultimo Pagamento</p>
                        <p className="font-medium">
                          {subscriptionData.subscription.last_payment_date 
                            ? new Date(subscriptionData.subscription.last_payment_date).toLocaleDateString('it-IT')
                            : '-'}
                          {subscriptionData.subscription.last_payment_amount && (
                            <span className="text-green-600 ml-2">
                              €{subscriptionData.subscription.last_payment_amount.toFixed(2)}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-text-muted">Email Fatturazione</p>
                        <p className="font-medium">
                          {subscriptionData.subscription.billing_email || user?.email || '-'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Canceled Notice */}
                  {subscriptionData.subscription?.canceled_at && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center gap-2 text-yellow-700">
                        <AlertTriangle className="w-5 h-5" />
                        <p>
                          Abbonamento disdetto il {new Date(subscriptionData.subscription.canceled_at).toLocaleDateString('it-IT')}.
                          {subscriptionData.is_pro && subscriptionData.subscription.current_period_end && (
                            <span className="block text-sm mt-1">
                              Rimarrà attivo fino al {new Date(subscriptionData.subscription.current_period_end).toLocaleDateString('it-IT')}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Cancel Button */}
                  {subscriptionData.is_pro && !subscriptionData.subscription?.canceled_at && (
                    <div className="border-t pt-6">
                      <p className="text-sm text-text-muted mb-3">
                        Puoi disdire l'abbonamento in qualsiasi momento. L'abbonamento rimarrà attivo fino alla fine del periodo corrente.
                      </p>
                      <Button 
                        variant="outline" 
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={async () => {
                          if (window.confirm('Sei sicuro di voler disdire l\'abbonamento Pro? Rimarrà attivo fino alla fine del periodo corrente.')) {
                            try {
                              await api.post('/subscriptions/cancel', { cancel_immediately: false });
                              toast.success('Abbonamento disdetto. Rimarrà attivo fino alla fine del periodo.');
                              fetchSubscriptionStatus();
                            } catch (error) {
                              toast.error('Errore nella disdetta');
                            }
                          }
                        }}
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        Disdici Abbonamento
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                /* No subscription - Show upgrade CTA */
                <div className="text-center py-8">
                  <Crown className="w-16 h-16 mx-auto mb-4 text-accent opacity-50" />
                  <h3 className="text-xl font-semibold mb-2">Non hai ancora un abbonamento Pro</h3>
                  <p className="text-text-muted mb-6 max-w-md mx-auto">
                    Passa a DalSud Pro per accedere a tutte le funzionalità avanzate: 
                    statistiche dettagliate, priorità nelle ricerche, supporto dedicato e molto altro.
                  </p>
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="text-center">
                      <p className="text-4xl font-bold text-accent">€29.90</p>
                      <p className="text-sm text-text-muted">/mese</p>
                    </div>
                  </div>
                  <Button 
                    size="lg" 
                    onClick={handleUpgradePlan}
                    className="gap-2 bg-accent hover:bg-accent/90"
                  >
                    <Crown className="w-5 h-5" />
                    Attiva DalSud Pro
                  </Button>
                  <p className="text-xs text-text-muted mt-4">
                    Pagamento sicuro con Stripe. Puoi disdire in qualsiasi momento.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ============================================
// LEDGER TAB COMPONENT
// ============================================


const LedgerTab = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/vendor/finance/balance')
      .then(res => setBalance(res.data))
      .catch(() => toast.error('Errore nel caricamento del saldo'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !balance) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
      </div>
    );
  }

  const isNegative = balance?.total_balance < 0;

  return (
    <div className="space-y-6">
      {/* Banner saldo negativo */}
      {isNegative && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">
            <strong>Saldo totale negativo</strong> dovuto a contestazioni o rimborsi.
            I prossimi pagamenti verranno utilizzati per coprire il debito.
          </p>
        </div>
      )}

      {/* Cards saldo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-border rounded-xl p-5">
          <p className="text-xs text-text-muted mb-1 flex items-center gap-1">
            <Wallet className="w-3 h-3" /> Saldo disponibile
          </p>
          <p className={`text-2xl font-bold ${balance?.available_balance < 0 ? 'text-red-600' : 'text-green-700'}`}>
            {balance ? `€${balance.available_balance.toFixed(2)}` : '—'}
          </p>
          <p className="text-xs text-text-muted mt-1">Movimenti confermati</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-5">
          <p className="text-xs text-text-muted mb-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> In revisione
          </p>
          <p className="text-2xl font-bold text-orange-600">
            {balance ? `€${Math.abs(balance.hold_balance).toFixed(2)}` : '—'}
          </p>
          <p className="text-xs text-text-muted mt-1">Hold dispute aperte</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-5">
          <p className="text-xs text-text-muted mb-1 flex items-center gap-1">
            <CircleDollarSign className="w-3 h-3" /> Saldo totale
          </p>
          <p className={`text-2xl font-bold ${isNegative ? 'text-red-600' : 'text-gray-800'}`}>
            {balance ? `€${balance.total_balance.toFixed(2)}` : '—'}
          </p>
          <p className="text-xs text-text-muted mt-1">Disponibile + revisione</p>
        </div>
      </div>

      {/* CTA dashboard finanziaria completa */}
      <div className="bg-white border border-border rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <History className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <p className="font-semibold text-sm">Dashboard Finanziaria completa</p>
            <p className="text-xs text-text-secondary mt-0.5">
              Storico completo dei movimenti, filtri per tipo, saldo dopo ogni transazione e gestione contestazioni.
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/produttore/finanze')}
          className="whitespace-nowrap gap-2 flex-shrink-0"
          size="sm"
        >
          <ArrowRight className="w-3.5 h-3.5" />
          Vai alla dashboard
        </Button>
      </div>
    </div>
  );
};

export default ProducerDashboard;
